import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";

const PAGE_SIZE = 50;

const SentEmails = () => {
  const dispatch = useDispatch();

  // ── Summary view state ──
  const [staffList, setStaffList] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // ── Detail view state ──
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  // ─── Fetch summary ───
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await adminApi.get("/admin/email/sent-summary");
      setStaffList(res.data?.staff || []);
    } catch (e) {
      const msg =
        e?.response?.data?.message || "Failed to load sent email summary";
      setSummaryError(msg);
      dispatch(showToast({ message: msg, type: "error" }));
    } finally {
      setSummaryLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ─── Fetch detail ───
  const fetchEmails = useCallback(
    async (staffId, nextOffset = 0) => {
      setEmailsLoading(true);
      setEmailsError(null);
      try {
        const res = await adminApi.get(
          `/admin/email/sent-by/${staffId}?limit=${PAGE_SIZE}&offset=${nextOffset}`,
        );
        setEmails(res.data?.emails || []);
        setTotal(res.data?.total || 0);
        setOffset(nextOffset);
      } catch (e) {
        const msg =
          e?.response?.data?.message || "Failed to load sent emails";
        setEmailsError(msg);
        dispatch(showToast({ message: msg, type: "error" }));
      } finally {
        setEmailsLoading(false);
      }
    },
    [dispatch],
  );

  const openDetail = (staff) => {
    setSelectedStaff(staff);
    setExpandedId(null);
    fetchEmails(staff.senderId, 0);
  };

  const closeDetail = () => {
    setSelectedStaff(null);
    setEmails([]);
    setTotal(0);
    setOffset(0);
    setExpandedId(null);
    setEmailsError(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeAgo = (d) => {
    if (!d) return "";
    const diff = Date.now() - new Date(d).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };

  const fullName = (s) =>
    [s.firstName, s.lastName].filter(Boolean).join(" ") ||
    s.senderEmail ||
    "Unknown";

  const initials = (s) => {
    const f = s.firstName?.[0] || "";
    const l = s.lastName?.[0] || "";
    return (f + l).toUpperCase() || "?";
  };

  const roleBadge = (role) => {
    const map = {
      super_admin: "bg-[#7c3aed] text-white border-[#6d28d9]",
      admin: "bg-[#d97706] text-white border-[#b45309]",
      manager: "bg-[#0d9488] text-white border-[#0f766e]",
      team_lead: "bg-[#1e3a5f] text-white border-[#0b1e33]",
      staff: "bg-[#2563eb] text-white border-[#1d4ed8]",
    };
    return map[role] || "bg-[#64748b] text-white border-[#475569]";
  };

  // ═══════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════════
  if (selectedStaff) {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
          <div className="flex items-center gap-3">
            <button
              onClick={closeDetail}
              className="w-10 h-10 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#1e3a5f] hover:border-[#1e3a5f] flex items-center justify-center transition-all"
              title="Back to staff list"
            >
              <i className="fas fa-arrow-left text-sm" />
            </button>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25">
              <span className="text-[12px] font-extrabold text-white font-mono">
                {initials(selectedStaff)}
              </span>
            </div>
            <div>
              <h2 className="font-syne text-lg font-extrabold text-[#0b1e33] uppercase tracking-tight">
                {fullName(selectedStaff)}
              </h2>
              <p className="text-[10px] text-[#475569] font-mono font-extrabold mt-0.5 uppercase tracking-wider">
                {selectedStaff.senderEmail} · {total} SENT
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchEmails(selectedStaff.senderId, offset)}
            className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
          >
            <i
              className={`fas fa-sync-alt mr-1.5 ${emailsLoading ? "animate-spin" : ""}`}
            />{" "}
            Refresh
          </button>
        </div>

        {/* Body */}
        {emailsLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : emailsError ? (
          <div className="p-10 text-center">
            <i className="fas fa-triangle-exclamation text-[#dc2626] text-3xl mb-3" />
            <p className="text-[#dc2626] font-syne font-extrabold uppercase tracking-wider text-sm mb-4">
              {emailsError}
            </p>
            <button
              onClick={() => fetchEmails(selectedStaff.senderId, offset)}
              className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33]"
            >
              <i className="fas fa-sync-alt mr-1.5" /> Retry
            </button>
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-20">
            <i className="fas fa-paper-plane text-4xl text-[#94a3b8] mb-3 opacity-30" />
            <p className="text-[#475569] font-syne font-extrabold uppercase tracking-wider text-sm">
              No emails sent yet
            </p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-[#f1f5f9] border-b-2 border-[#e2e8f0]">
              <div className="col-span-4 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                To
              </div>
              <div className="col-span-5 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                Subject
              </div>
              <div className="col-span-3 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                Sent
              </div>
            </div>

            {emails.map((em) => {
              const open = expandedId === em.id;
              return (
                <div
                  key={em.id}
                  className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                >
                  <button
                    onClick={() => setExpandedId(open ? null : em.id)}
                    className="w-full text-left px-5 py-3.5 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center"
                  >
                    <div className="md:col-span-4 flex items-center gap-2 min-w-0">
                      <i className="fas fa-envelope text-[#1e3a5f] text-xs flex-shrink-0" />
                      <span className="text-[12px] text-[#0f172a] font-extrabold truncate">
                        {em.recipientEmail}
                      </span>
                    </div>
                    <div className="md:col-span-5 text-[12px] text-[#475569] font-bold truncate">
                      {em.subject || "(No subject)"}
                    </div>
                    <div className="md:col-span-3 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-[#64748b] font-mono font-bold whitespace-nowrap">
                        {formatDate(em.sentAt)}
                      </span>
                      <i
                        className={`fas fa-chevron-${open ? "up" : "down"} text-[#94a3b8] text-[10px]`}
                      />
                    </div>
                  </button>

                  {open && (
                    <div className="px-5 pb-5">
                      <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-4">
                        <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-[#e2e8f0]">
                          <span className="text-[10px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">
                            <i className="fas fa-user mr-1" /> To:
                          </span>
                          <span className="text-[11px] text-[#0f172a] font-extrabold">
                            {em.recipientEmail}
                          </span>
                          <span className="text-[10px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider ml-auto">
                            <i className="fas fa-clock mr-1" />{" "}
                            {formatTimeAgo(em.sentAt)}
                          </span>
                        </div>
                        <div className="text-[10px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">
                          <i className="fas fa-tag mr-1" /> Subject
                        </div>
                        <div className="text-[13px] text-[#0f172a] font-extrabold mb-4">
                          {em.subject || "(No subject)"}
                        </div>
                        <div className="text-[10px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">
                          <i className="fas fa-align-left mr-1" /> Body
                        </div>
                        <pre className="whitespace-pre-wrap break-words text-[12px] text-[#0f172a] font-sans leading-relaxed bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3 max-h-[400px] overflow-y-auto">
                          {em.body || "(empty)"}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5 border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
                <span className="text-[11px] text-[#475569] font-mono font-extrabold">
                  Page {currentPage} of {totalPages} · {total} total
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() =>
                      fetchEmails(selectedStaff.senderId, offset - PAGE_SIZE)
                    }
                    disabled={offset === 0 || emailsLoading}
                    className="px-3 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[10px] font-extrabold hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-chevron-left text-[9px] mr-1" /> Prev
                  </button>
                  <button
                    onClick={() =>
                      fetchEmails(selectedStaff.senderId, offset + PAGE_SIZE)
                    }
                    disabled={offset + PAGE_SIZE >= total || emailsLoading}
                    className="px-3 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[10px] font-extrabold hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Next <i className="fas fa-chevron-right text-[9px] ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SUMMARY VIEW
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            SENT EMAILS
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            Emails sent by staff · click a member to see their history
          </p>
        </div>
        <button
          onClick={fetchSummary}
          disabled={summaryLoading}
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33] disabled:opacity-50"
        >
          <i
            className={`fas fa-sync-alt mr-1.5 ${summaryLoading ? "animate-spin" : ""}`}
          />{" "}
          Refresh
        </button>
      </div>

      {/* Body */}
      {summaryLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : summaryError ? (
        <div className="p-10 text-center">
          <i className="fas fa-triangle-exclamation text-[#dc2626] text-3xl mb-3" />
          <p className="text-[#dc2626] font-syne font-extrabold uppercase tracking-wider text-sm mb-4">
            {summaryError}
          </p>
          <button
            onClick={fetchSummary}
            className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33]"
          >
            <i className="fas fa-sync-alt mr-1.5" /> Retry
          </button>
        </div>
      ) : staffList.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-paper-plane text-[#94a3b8] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">
            No emails sent yet
          </p>
          <p className="text-[#64748b] text-[11px] font-mono font-bold">
            Once staff start sending emails, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="divide-y-2 divide-[#f1f5f9]">
          {staffList.map((s) => (
            <button
              key={`${s.senderId}-${s.senderEmail}`}
              onClick={() => openDetail(s)}
              className="w-full text-left p-4 md:p-5 bg-white hover:bg-[#f8fafc] transition-all flex items-center gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25 flex-shrink-0">
                <span className="text-[12px] font-extrabold text-white font-mono">
                  {initials(s)}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-syne text-sm font-extrabold text-[#0f172a] truncate">
                    {fullName(s)}
                  </span>
                  {s.role && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-mono font-extrabold uppercase tracking-wider border-2 border-b-[2px] ${roleBadge(s.role)}`}
                    >
                      {s.role.replace(/_/g, " ")}
                    </span>
                  )}
                  {s.staffType && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-mono font-extrabold uppercase tracking-wider bg-[#f1f5f9] text-[#475569] border-2 border-[#e2e8f0]">
                      {s.staffType}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#64748b] font-mono font-bold truncate mt-0.5">
                  <i className="fas fa-envelope mr-1" />
                  {s.senderEmail}
                </div>
              </div>

              <div className="hidden sm:flex flex-col items-end flex-shrink-0">
                <span className="font-syne text-lg font-extrabold text-[#1e3a5f]">
                  {s.totalSent}
                </span>
                <span className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">
                  sent
                </span>
              </div>

              <div className="hidden md:flex flex-col items-end flex-shrink-0 w-40">
                <span className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">
                  Last sent
                </span>
                <span className="text-[11px] text-[#0f172a] font-bold">
                  {formatTimeAgo(s.lastSentAt)}
                </span>
              </div>

              <i className="fas fa-chevron-right text-[#94a3b8] group-hover:text-[#1e3a5f] transition-all text-xs flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SentEmails;